import { useCallback, startTransition } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useUserPartnerIds } from "@/query/hooks/useUserPartners";
import { useCompanyTimezone } from "@/lib/datetime/companyTimezone";
import {
  useRecapDataContext,
  useRecapStateContext,
  useFocusNewOrderSku,
} from "../contexts/RecapContext";
import {
  computeVisibilityPatch,
  type PreOrderTarget,
  type RecapStateView,
  type VisibilityPatch,
} from "../services/preOrderVisibilityService";
import { useWaitForPreOrderInData } from "./useWaitForPreOrder";
import type { PreOrder } from "@/app/api/types/preOrder";
import { hasAiChanges } from "../services/recapService";

/**
 * Search-param key used to drive the recap row highlight + scroll. Any write
 * to this param triggers the effect in `RecapShell` that calls
 * `chatPanel.highlightPreOrder`. The param is left in the URL so reload /
 * back-navigation re-anchors to the same pre-order.
 */
export const HIGHLIGHT_PARAM = "pre-order";

export interface GoToOptions {
  /** Push a new history entry instead of replacing. Default true. */
  pushHistory?: boolean;
  /** Focus the SKU input on the target's flow row. Default true. */
  focusSku?: boolean;
  /** Suppress the "filtersAdjusted" toast even when filters were widened. */
  silentToast?: boolean;
  /** Override the wait-for-data timeout (default 1500 ms). */
  waitTimeoutMs?: number;
}

/**
 * Unified entry point for "go to a pre-order" — orchestrates make-visible
 * state changes, wait-for-data arrival, and a URL write that drives the
 * scroll + highlight effect.
 *
 * Behavior contract:
 * - Compute the minimal patch via `computeVisibilityPatch`. Only mutate the
 *   filter dimensions that would currently hide the target.
 * - Apply mutations in one batch, then await the target appearing in
 *   `data.preOrders` (1500 ms timeout) so the row is rendered before the
 *   highlight effect fires.
 * - Write `?pre-order=<id>` to the URL. `RecapShell` listens for this param
 *   and triggers `chatPanel.highlightPreOrder` — keeping scroll + tint
 *   coupled to a URL change so deep-links and reloads behave the same.
 *
 * Callers that want to land in a specific recap mode set it directly on
 * `state` before invoking this hook (the visibility service evaluates dates
 * against the current mode as-is).
 */
export function useGoToPreOrder() {
  const { t } = useTranslation();
  const state = useRecapStateContext();
  const data = useRecapDataContext();
  const focusNewOrderSku = useFocusNewOrderSku();
  const userPartnerIds = useUserPartnerIds();
  // Threaded into `computeVisibilityPatch` so the day-window check
  // reads order_date / delivery_date in the company tz instead of UTC
  // — see `preOrderVisibilityService.dayOnly` for why slicing the raw
  // UTC prefix snaps an "00:00 in company tz" order to the previous
  // calendar day and bounces the recap there after an AI accept.
  const tz = useCompanyTimezone();
  const waitForPreOrder = useWaitForPreOrderInData(data.preOrders);
  const [, setSearchParams] = useSearchParams();

  return useCallback(
    async (target: PreOrderTarget, opts: GoToOptions = {}): Promise<void> => {
      const {
        pushHistory = true,
        focusSku = true,
        silentToast = false,
        waitTimeoutMs = 1500,
      } = opts;

      if (target.isDeleted) {
        toast.warning(t("ordersRecap.targetPreOrderDeleted"));
        return;
      }

      const currentState: RecapStateView = {
        recapMode: state.recapMode,
        rangeMode: state.rangeMode,
        selectedDate: state.selectedDate,
        endDate: state.endDate,
        startDateTime: state.startDateTime,
        endDateTime: state.endDateTime,
        statusFilter: state.statusFilter,
        searchQuery: state.searchQuery,
        filters: state.filters,
        showDeletedOnly: state.showDeletedOnly,
      };

      const { patch, adjusted } = computeVisibilityPatch(
        currentState,
        target,
        { userPartnerIds: userPartnerIds ?? null },
        tz,
      );

      if (adjusted) {
        if (!silentToast) {
          toast.info(t("ordersRecap.filtersAdjusted"));
        }

        startTransition(() => {
          applyPatch(patch, state);
        });

        // Wait for the target to land in data after the state change
        // triggers a refetch. On timeout we still write the URL — the
        // RecapShell effect picks up whatever is rendered.
        await waitForPreOrder(target.id, waitTimeoutMs);
      }

      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(HIGHLIGHT_PARAM, target.id);
          return next;
        },
        { replace: !pushHistory },
      );

      if (focusSku) {
        focusNewOrderSku(target.id);
      }
    },
    // data.preOrders intentionally omitted: the wait utility re-reads it on
    // every render via an internal ref, so the callback identity stays stable.
    [
      state,
      focusNewOrderSku,
      userPartnerIds,
      tz,
      t,
      waitForPreOrder,
      setSearchParams,
    ],
  );
}

function applyPatch(
  patch: VisibilityPatch,
  state: ReturnType<typeof useRecapStateContext>,
) {
  // SET_RANGE_MODE → "exact" reducer also clears startDateTime/endDateTime/
  // endDate. Apply rangeMode first so subsequent setters take effect on the
  // already-cleared shape.
  if (patch.rangeMode !== undefined && patch.rangeMode !== state.rangeMode) {
    state.setRangeMode(patch.rangeMode);
  }
  if (patch.selectedDate !== undefined) {
    state.setSelectedDate(patch.selectedDate);
  }
  if (patch.endDate !== undefined) {
    state.setEndDate(patch.endDate);
  }
  if (patch.startDateTime !== undefined || patch.endDateTime !== undefined) {
    state.setTimeRange(patch.startDateTime, patch.endDateTime);
  }
  if (patch.statusFilter !== undefined) {
    state.setStatusFilter(patch.statusFilter);
  }
  if (patch.filters !== undefined) {
    state.setFilters(patch.filters);
  }
  if (patch.searchQuery !== undefined) {
    state.setSearchQuery(patch.searchQuery);
  }
  if (patch.showDeletedOnly !== undefined) {
    state.setShowDeletedOnly(patch.showDeletedOnly);
  }
}

/**
 * Build a `PreOrderTarget` from a recap PreOrder. Convenience helper for
 * call sites that already have the full PreOrder object.
 */
export function preOrderToTarget(
  po: {
    id: string;
    partner_id?: string | null;
    partner?: { id?: string; name?: string } | null;
    order_date?: string | null;
    delivery_date?: string | null;
    status?: number | null;
    source_type?: number | null;
    employee_id?: string | null;
    location_id?: string | null;
    deleted_at?: string | null;
    pre_order_metadata?: PreOrder["pre_order_metadata"];
  },
  partnerName?: string,
): PreOrderTarget {
  return {
    id: po.id,
    partnerId: po.partner_id ?? po.partner?.id ?? null,
    partnerName: partnerName ?? po.partner?.name ?? "",
    orderDate: po.order_date ?? null,
    deliveryDate: po.delivery_date ?? null,
    status: po.status ?? null,
    // Bucket flags the numeric status can't express, so go-to-pre-order can
    // widen the status filter onto export-failed / AI-proposal rows.
    isExportFailed: po.pre_order_metadata?.export?.status === "failed",
    hasAiChanges: hasAiChanges(po as PreOrder),
    sourceType: po.source_type ?? null,
    employeeId: po.employee_id ?? null,
    locationId: po.location_id ?? null,
    isDeleted: po.deleted_at != null,
  };
}
